import React, {
  createContext,
  useState,
  useContext,
  ReactNode,
  useMemo,
} from 'react';

import { scrollToEndOfElement } from './product-configuration.utils';

interface ScrollContextProps {
  hasRequiredOptionScrolled: boolean;
  hasNonRequiredOptionScrolled: boolean;
  setRequiredOptionScrolled: (value: boolean) => void;
  setNonRequiredOptionScrolled: (value: boolean) => void;
  scrollRequiredOption: (parentId: string) => void;
  scrollNonRequiredOption: (parentId: string) => void;
}

const ScrollContext = createContext<ScrollContextProps | undefined>(undefined);

export const ScrollProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [hasRequiredOptionScrolled, setHasRequiredOptionScrolled] =
    useState(false);
  const [hasNonRequiredOptionScrolled, setHasNonRequiredOptionScrolled] =
    useState(false);

  const scrollRequiredOption = (parentId: string) => {
    if (!hasRequiredOptionScrolled) {
      scrollToEndOfElement({ elementId: parentId });
      setHasRequiredOptionScrolled(true);
    }
  };

  const scrollNonRequiredOption = (parentId: string) => {
    if (!hasNonRequiredOptionScrolled) {
      scrollToEndOfElement({ elementId: parentId, offsetHeader: true });
      setHasNonRequiredOptionScrolled(true);
    }
  };

  const value = useMemo(
    () => ({
      hasRequiredOptionScrolled,
      hasNonRequiredOptionScrolled,
      setRequiredOptionScrolled: setHasRequiredOptionScrolled,
      setNonRequiredOptionScrolled: setHasNonRequiredOptionScrolled,
      scrollRequiredOption,
      scrollNonRequiredOption,
    }),
    [
      hasRequiredOptionScrolled,
      hasNonRequiredOptionScrolled,
      setHasRequiredOptionScrolled,
      setHasNonRequiredOptionScrolled,
      scrollRequiredOption,
      scrollNonRequiredOption,
    ]
  );

  return (
    <ScrollContext.Provider value={value}>{children}</ScrollContext.Provider>
  );
};

export const useScrollContext = (): ScrollContextProps => {
  const context = useContext(ScrollContext);
  if (!context) {
    throw new Error('useScrollContext must be used within a ScrollProvider');
  }
  return context;
};
